import TimePeriod from '../../models/TimePeriod';
import Organisation from '@/models/Organisation';
import { ManagerUser } from '../fund/types';
import { SummaryAnalysis } from '@/modules/analysis/types';

export interface DataTableOptions {
  page: number;
  itemsPerPage: number;
  sortBy: string[];
  sortDesc: boolean[];
  groupBy: string[];
  groupDesc: boolean[];
  multiSort: boolean;
  mustSort: boolean;
}

export interface Paginated<T> {
  data: T[];
  meta: PaginationMeta;
  links: PaginationLinks;
}

export interface PaginationLinks {
  first?: string;
  last?: string;
  prev?: string;
  next?: string;
}

export interface PaginationMeta {
  current_page: number;
  from?: number;
  last_page?: number;
  path?: string;
  per_page: number;
  to?: number;
  total?: number;
  sortBy?: string[];
  sortDesc?: boolean[];
}

export interface AppliedAnalysisFilter {
  assetClassIds?: string[];
  reportingPeriodIds?: string[];
  pensionSchemeIds?: string[];
  fundIds?: string[];
  assetManagerIds?: string[];
  subFundIds?: string[];
  pensionSchemeCount?: number;
  instanceId: number;
}

export type Tab = {
  name: string;
  link: string;
  badge_count?: number;
};

export type TableHeader = {
  text: string;
  value: string;
  sortable?: boolean;
  class?: string;
  align?: string;
  filterable?: boolean;
  width?: string;
};

export type ContentType = {
  send_request?: boolean;
  assign_user?: boolean;
  search?: boolean;
  reporting_period?: boolean;
  filters?: boolean;
  table_headers?: TableHeader[];
  add_fund?: boolean;
};
export interface TabContent {
  [key: string]: ContentType;
}

export type NavigationDrawer = {
  title: string;
  icon: string;
  color?: string;
  link: string;
};

export type TypeDetails = {
  id: string;
  name: string;
  slug: string;
};

export type Manager = {
  id: string;
  name: string;
};

export type Entity = {
  name: string;
  nature: TypeDetails;
  strategy: TypeDetails;
  assetManager: Manager;
  assetManagerUser: ManagerUser;
};

export type Fund = {
  id: string;
  timePeriod: TimePeriod;
  entity_type: string;
  entity: Entity;
  client: string;
  children: {
    data: Fund[];
  };
  funds: Fund[];
};

export type OrganizationFunds = {
  timePeriod: TimePeriod;
  parent: string;
  entity_type: string;
  entity: Entity;
  children: {
    data: Fund[];
  };
};

export type PensionScheme = {
  name: string;
  timePeriod?: TimePeriod;
  rootInvestements: {
    data: Fund[];
  };
};

export type PensionSchemes = {
  name?: string;
  childOrganizations?: {
    data: PensionScheme[];
  };
};

export enum UnitType {
  GBP = 'gbp',
  PERCENT = 'percent',
  BPS = 'bps'
}
export interface BehaviourEntity {
  name: string;
}
export interface RootCost extends Cost {
  type: string;
  behaviour?: Behaviour[];
  serviceCost?: number;
  absServiceCost?: number;
  denominatedServiceCost?: number;
  fiduciaryServiceCost?: number;
  absFiduciaryServiceCost?: number;
  denominatedFiduciaryServiceCost?: number;
}

export interface FundCost extends Cost {
  rootInvestmentId: string;
  parentId: string;
  strategyId: string;
  productTypeId: string;
  natureId: string;
  strategyName: string;
  productTypeName: string;
  natureName: string;
  annualisationFactor?: number;
  deAnnualisationFactor?: number;
  conversionRate?: number;
  currency?: string;
  costCheck?: boolean;
  notes?: string;
  investmentPeriodStart?: string;
  investmentPeriodEnd?: string;
  investmentPeriodRange?: string;
}

export interface Cost {
  investmentId?: string;
  entityId: string;
  name: string;
  amId: string;
  amName: string;
  totalCost?: number;
  absTotalCost?: number;
  annualizedTotalCost?: number;
  denominatedTotalCost?: number;
  transCost?: number;
  absTransCost?: number;
  annualizedTransCost?: number;
  denominatedTransCost?: number;
  annualManagementCost?: number;
  absAnnualManagementCost?: number;
  annualizedAnnualManagementCost?: number;
  denominatedAnnualManagementCost?: number;
  investmentManagementCost?: number;
  absInvestmentManagementCost?: number;
  annualizedInvestmentManagementCost?: number;
  denominatedInvestmentManagementCost?: number;
  afeCost?: number;
  absAfeCost?: number;
  annualizedAfeCost?: number;
  denominatedAfeCost?: number;
  propertyCost?: number;
  absPropertyCost?: number;
  annualizedPropertyCost?: number;
  denominatedPropertyCost?: number;
  adminCost?: number;
  absAdminCost?: number;
  annualizedAdminCost?: number;
  denominatedAdminCost?: number;
  grcCost?: number;
  absGrcCost?: number;
  annualizedGrcCost?: number;
  denominatedGrcCost?: number;
  dccsCost?: number;
  absDccsCost?: number;
  annualizedDccsCost?: number;
  denominatedDccsCost?: number;
  lendingCost?: number;
  absLendingCost?: number;
  annualizedLendingCost?: number;
  denominatedLendingCost?: number;
  otherFees?: number;
  absOtherFees?: number;
  annualizedOtherFees?: number;
  denominatedOtherFees?: number;
  ongoingCharges?: number;
  absOngoingCharges?: number;
  annualizedOngoingCharges?: number;
  denominatedOngoingCharges?: number;
  directOngoingCharges?: number;
  absDirectOngoingCharges?: number;
  annualizedDirectOngoingCharges?: number;
  denominatedDirectOngoingCharges?: number;
  indirectOngoingCharges?: number;
  absIndirectOngoingCharges?: number;
  annualizedIndirectOngoingCharges?: number;
  denominatedIndirectOngoingCharges?: number;
  oneOffCost?: number;
  absOneOffCost?: number;
  annualizedOneOffCost?: number;
  denominatedOneOffCost?: number;
  ancillaryCost?: number;
  absAncillaryCost?: number;
  annualizedAncillaryCost?: number;
  denominatedAncillaryCost?: number;
  perfFees?: number;
  absPerfFees?: number;
  annualizedPerfFees?: number;
  denominatedPerfFees?: number;
  grossReturn?: number;
  absGrossReturn?: number;
  netReturn?: number;
  netReturnCalculated?: boolean;
  absNetReturn?: number;
  totalAum?: number;
  providedAssetBaseValue?: number;
  providedAum?: number;
  denominatedAum?: number;
  denominatedSlug?: string;
  reportingPeriod?: string;
}
export interface AllAnalysisData {
  fundData: FundCost[];
  pensionData: RootCost[];
  assetManagerCount: number;
  summaryFundCount: number;
  schemeCount: number;
  subFundCount: number;
  totalAum: number | string;
  totalCost: string | number;
  absTotalAum: number;
  absTotalCost: number;
  aumChartData: AumChartData[];
  citChartData: CitChartData[];
  cbtChartData: CbtChartData[];
  citBarChartData?: CitBarChartData;
  cvpChartData?: CvPChartData;
  legend: string[];
  legendList: Array<{ name: string; color: string }>;
  summary: SummaryAnalysis;
  colorTheme: string[];
  fundIdsByAum: FundCost[];
  fundIdsByCost: FundCost[];
}

export type FundAnalysisData = {
  summaryFundCount: number;
  summary: SummaryAnalysis;
  fundData: FundCost[];
};

export interface QualitativeAnalysis {
  amName: string;
  name?: string;
  willingness: number;
  ability: number;
  responsiveness: number;
  notes: string;
}

// { strategyName: key, fundsInfo: value }
export interface QuantitativeAnalysis {
  strategyName: string;
  fundsInfo: Array<{
    fundName: string;
    productTypeName: string;
    amName: string;
    totalCost: number;
    transCost: number;
    perfFees: number;
    totalAum: number | true;
    formattedAum: string | number;
    grossReturn: number;
    netReturn: number;
  }>;
}

export interface AumChartData {
  value: number;
  name: string;
}

export interface CitChartData {
  name: string;
  value: number[];
}
export interface CbtChartData {
  name: string;
  value: number[];
}
export interface CbsChartData {
  name: string;
  data: number[];
  type: string;
}
export interface CvPChartData {
  data: Array<{
    name: string;
    value: (number | null)[];
  }>;
}

export interface CitBarChartData {
  keys: string[];
  data: any[];
  aumPercent: BarChartSeriesItem[];
  costPercent: BarChartSeriesItem[];
}
export interface BarChartSeriesItem {
  name: string;
  value: number;
  itemStyle: {
    color: string;
  };
}

export interface AdvancedAnalysis {
  aumChartData: AumChartData[];
  citChartData: CitChartData[];
  cbsChartData: CbsChartData[];
  legend: string[];
  legendList: Array<{ name: string; color: string }>;
  colorTheme: string[];
}

export interface Behaviour {
  name: string;
  willingness?: number;
  ability?: number;
  responsiveness?: number;
  notes?: string;
}

export interface summaryApiParam {
  investmentId: string[];
  submissionIds?: string[];
  fundIds: string[];
  source?: string[];
  rootOrgId: string;
  drilldownLevel: string;
}

export interface AnalysisParam {
  assetClassIds?: string[];
  reportingPeriodIds?: string[];
  pensionSchemeIds?: string[];
  fundIds?: string[];
  assetManagerIds?: string[];
  rootOrgId: string;
  status: string;
  source?: string;
  drilldownLevel: string;
  subFundIds?: string[];
}

export interface AnalysisParamWithFilter extends AnalysisParam {
  responseFilter: ResponseFilter;
}

export interface FundAnalysisParam {
  assetClassIds?: string[];
  reportingPeriodIds?: string[];
  pensionSchemeIds?: string[];
  fundIds?: string[];
  assetManagerIds?: string[];
  rootOrgId: string;
  status?: string;
  source?: string;
  drilldownLevel?: string;
  subFundIds?: string[];
  page: number;
  limit: number;
}

export interface ResponseFilter {
  fundData: boolean;
  underlyingfundData?: boolean;
  pensionData?: boolean;
  advancedAnalysis?: boolean;
  topFunds?: boolean;
  summary?: boolean;
  excludeFundsWithNoAum?: boolean;
  excludeSummaryAnalysis?: boolean;
  amUserDetails?: boolean;
  reportingPeriodDetails?: boolean;
  submissionDetails?: boolean;
  putIncompleteDataInNewSheet?: boolean;
}

export type AnalysisPensionScheme = {
  id: string;
  parent_id: string;
  entity_id: string;
  entity_type: string;
  time_period_id: string;
  meta: any[];
  entity: Entity;
  timePeriod: TimePeriod;
  managers: Organisation[];
};

export type AnalysisFunds = {
  id: string;
  root_id: string;
  entity_id: string;
  name: string;
  product_type_id: string;
};

export const AssetBases = [
  { slug: 'capital_drawdown', name: 'Capital Drawdown', color: '#FC4C81' },
  { slug: 'commitment', name: 'Commitment', color: '#f57c00' },
  { slug: 'exposure', name: 'Exposure', color: '#467FEF' },
  { slug: 'aum', name: 'AuM', color: '#28587b' }
];
